import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const toggleBtnRef = useRef(null);
  const menuId = "mobile-nav";

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close on Esc, lock body scroll while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);

    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener("keydown", onKey);
      };
    }

    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // If viewport grows to md+, ensure menu is closed
  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) {
        setOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const linkCls = ({ isActive }) =>
    `block transition-colors ${
      isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-sky-600"
    }`;

  return (
    <header className="fixed top-0 left-0 w-full bg-white/90 backdrop-blur z-50 border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Flexible height on mobile so the tagline can show */}
        <div className="min-h-20 md:h-24 py-2 flex items-center justify-between">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img
              src="/images/Logo.png"
              alt="PERA logo"
              className="w-14 h-14 md:w-20 md:h-20 rounded-lg border-2 border-[#8d1b1e] p-1 bg-white shrink-0"
            />
            <div className="flex flex-col items-start min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {/* Company Name Image */}
                <img
                  src="/images/Name2.png"
                  alt="PERA Engineering"
                  className="h-8 md:h-12 object-contain"
                />
              </div>
              {/* Tagline — always visible, smaller on mobile */}
              <p
                className="inline-block text-sm sm:text-sm md:text-lg px-14 py-0.5 bg-sky-300 text-black rounded-sm shadow leading-tight"
                style={{ fontFamily: '"Monotype Corsiva", cursive' }}
              >
                We Convert Land into Land Mark
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-lg font-medium">
            <NavLink to="/" className={linkCls} end>
              Home
            </NavLink>
            <NavLink to="/about" className={linkCls}>
              About us
            </NavLink>
            <NavLink to="/projects" className={linkCls}>
              Our Projects
            </NavLink>
            <NavLink to="/contact" className={linkCls}>
              Contact us
            </NavLink>
          </nav>

          {/* Mobile menu button */}
          <button
            ref={toggleBtnRef}
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-gray-200 bg-white"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls={menuId}
          >
            {!open ? (
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.41L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.3 6.29-6.3 1.42 1.41z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Scrim */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/30"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile panel */}
      <div
        id={menuId}
        className={`md:hidden fixed left-0 right-0 top-20 md:top-24 origin-top ${
          open ? "scale-y-100 opacity-100" : "pointer-events-none scale-y-95 opacity-0"
        } bg-white border-t border-gray-200 shadow-lg transition-all`}
        style={{ transformOrigin: "top" }}
      >
        <div className="max-h-[calc(100dvh-5rem)] md:max-h-[calc(100dvh-6rem)] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex flex-col gap-4 text-base font-medium">
              <NavLink to="/" className={linkCls} end>
                Home
              </NavLink>
              <NavLink to="/about" className={linkCls}>
                About us
              </NavLink>
              <NavLink to="/projects" className={linkCls}>
                Our Projects
              </NavLink>
              <NavLink to="/contact" className={linkCls}>
                Contact us
              </NavLink>
            </nav>

            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
              <Link
                to="/contact"
                className="flex-1 text-center px-4 py-2 rounded-md bg-sky-600 text-white font-semibold"
                onClick={() => setOpen(false)}
              >
                Get in touch
              </Link>
            </div>

            {/* Safe-area spacer for iOS */}
            <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
          </div>
        </div>
      </div>
    </header>
  );
}